/**
 * Reading the server's split preview — labels and the sentences that explain
 * what it could not calculate.
 *
 * The arithmetic is NOT here. `POST /products/:id/split/preview` returns every
 * figure and the formula behind it, produced by the same pass that will store
 * the results (`cgt-backend/src/products/lims-calculations.ts`). This module
 * only turns that answer into what the dialog says, which is the part worth
 * testing without a DOM — `vitest.config.ts` runs `.test.ts` under `src` in a
 * node environment, so a component test would silently never run.
 */

import type {
  LimsResultRef,
  SplitPreview,
  SplitPreviewChild,
  SplitPreviewRow,
  SplitPreviewUnavailable,
} from '@/types/products'
import { LIMS_RESULT_LABELS, LIMS_RESULT_ORDER, type LimsResultKey } from '@/lib/limsResults'

/**
 * A result's label, preferring this app's wording over the server's.
 *
 * Both name the same fifteen keys, but the labels are presentation and the two
 * have drifted apart once already ("— single platform" here, ", single
 * platform" there). The KEY is the contract; the server's label is the
 * fallback for one this build does not know about.
 */
export function limsLabelFor(ref: LimsResultRef): string {
  return LIMS_RESULT_LABELS[ref.key as LimsResultKey] ?? ref.label
}

/**
 * The rows in this app's reading order — grouped by marker, TNC then CD34 then
 * CD3, the same order the LIMS details dialog uses.
 *
 * The server answers in ITS canonical order, which puts the five measured
 * results first. Both are defensible; agreeing with the other screen that
 * shows these numbers matters more. The order also keeps each result after the
 * ones its formula refers to, so the panel reads top to bottom.
 */
export function orderPreviewRows(rows: SplitPreviewRow[]): SplitPreviewRow[] {
  const position = new Map(LIMS_RESULT_ORDER.map((key, i) => [key as string, i]))
  return [...rows].sort(
    (a, b) => (position.get(a.key) ?? Infinity) - (position.get(b.key) ?? Infinity),
  )
}

/** "TNC/mL (×10⁶), CD34 (%) and CD3/mcL" — a list a sentence can hold. */
export function joinLabels(refs: LimsResultRef[]): string {
  const labels = refs.map(limsLabelFor)
  if (labels.length === 0) return ''
  if (labels.length === 1) return labels[0]
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
}

/**
 * Why nothing can be previewed, said in terms of what to do about it.
 *
 * `no_measurements` is the one that needed spelling out: products made before
 * the LIMS results existed were deliberately not backfilled, so a split of one
 * of them recalculates nothing. Without saying so, an empty panel reads as the
 * feature being broken.
 *
 * The measurements are read from the form fields placed from the Field Library
 * entries with the dose system names (`tnc_per_ml`, …), on this form or an
 * earlier one in the procedure. The split itself is refused until they are
 * found — `LimsInputsNotFound` lists exactly which — so this only has to say
 * where the values come from.
 */
export function unavailableMessage(reason: SplitPreviewUnavailable): string {
  switch (reason) {
    case 'no_source_volume':
      return 'This product has no recorded volume, so there is nothing to divide its results across. Set one from Update product first.'
    case 'no_measurements':
      return 'No lab measurement was found on this form or any earlier form in the procedure, so there is nothing to recalculate. Measurements are read from the fields placed from the Field Library entries with the dose system names (tnc_per_ml, cd34_per_mcl, cd34_pct, cd3_per_mcl, cd3_pct), filled by the laboratory or by hand.'
    case 'no_aliquot_volumes':
      return 'Give each aliquot a volume to see what it would be given.'
  }
}

/**
 * What the source product's missing measurements cost, when it has some but
 * not all five.
 *
 * Null when nothing is missing — the caller renders nothing rather than a
 * reassuring sentence nobody needs to read.
 */
export function missingMeasuredMessage(preview: SplitPreview): string | null {
  const missing = preview.missingMeasured.filter(m => m.blocks.length > 0)
  if (preview.unavailable === 'no_measurements' || missing.length === 0) return null
  const parts = missing.map(
    m => `no ${limsLabelFor(m)} was measured, so ${joinLabels(m.blocks)} cannot be calculated`,
  )
  return `On this product ${parts.join('; ')}.`
}

/**
 * The per-kg doses left out for want of a recipient weight.
 *
 * Kept separate from the missing MEASUREMENTS because the fix is somewhere
 * else entirely — the patient chart, not the lab.
 */
export function missingWeightMessage(preview: SplitPreview): string | null {
  if (preview.missingForWeight.length === 0) return null
  return `No recipient weight is recorded on this procedure, so ${joinLabels(
    preview.missingForWeight,
  )} are left out rather than shown as zero. Enter Recipient Weight (kg) on the patient chart.`
}

/**
 * How the source's volume splits into cells and cryomedia.
 *
 * Every formula runs on the CELL volume, so a panel that quoted the product
 * volume next to "× 42" would look like it had dropped a factor of two.
 */
export function cellVolumeNote(preview: SplitPreview): string | null {
  const { volumeMl, cellVolumeMl } = preview.source
  if (volumeMl == null || cellVolumeMl == null) return null
  const pct = Math.round(preview.cryomediaFraction * 100)
  return `${volumeMl} mL of product = ${cellVolumeMl} mL of cells (${pct}% cryomedia). Every formula below uses the cell volume.`
}

/** The aliquot's own volumes, for its section heading. */
export function childVolumeNote(child: SplitPreviewChild): string {
  if (child.volumeMl == null) return 'no volume set'
  if (child.cellVolumeMl == null) return `${child.volumeMl} mL`
  return `${child.volumeMl} mL — ${child.cellVolumeMl} mL of cells`
}

/**
 * What the dialog's per-aliquot row says in the LIMS column.
 *
 * Deliberately not a figure: picking one of fifteen results to stand for the
 * rest would invite it to be read as "the" dose. It says how many were
 * recalculated and sends the operator to the panel that shows them.
 */
export function limsColumnSummary(
  preview: SplitPreview | undefined,
  child: SplitPreviewChild | undefined,
  isLoading: boolean,
): { text: string; tone: 'muted' | 'warning' } {
  if (isLoading) return { text: 'Calculating…', tone: 'muted' }
  if (!preview) return { text: 'Recalculated on save', tone: 'muted' }
  if (preview.unavailable === 'no_measurements') {
    return { text: 'No LIMS results on the source', tone: 'warning' }
  }
  const count = child?.rows.length ?? 0
  if (count === 0) return { text: 'Nothing to recalculate', tone: 'warning' }
  return { text: `${count} result${count === 1 ? '' : 's'} recalculated`, tone: 'muted' }
}
